import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { roleLabel } from "../lib/roleLabels";
import { fetchBillingAccess } from "../lib/billingAccess";
import {
  LayoutDashboard, TrendingUp, CreditCard,
  Users, ScrollText, LogOut, ChevronRight, Shield, PhoneCall, FileBarChart, ClipboardList, Bug, CalendarDays, Search, MessageSquareWarning,
  Receipt
} from "lucide-react";

const NAV = {
  sales: [
    { label: "Referral Intake Dashboard", to: "/dashboard/sales", icon: TrendingUp },
    { label: "New Referral Intake", to: "/referral-intake", icon: ClipboardList },
    { label: "Manual Referral Entry", to: "/manual-referral", icon: ClipboardList },
    { label: "CRM Queue", to: "/crm-queue", icon: PhoneCall },
    { label: "SMS Needs Attention", to: "/sms-needs-attention", icon: MessageSquareWarning },
    { label: "Patient Search", to: "/patient-search", icon: Search },
    { label: "Network Scheduling", to: "/network-scheduling", icon: CalendarDays },
    { label: "Referral Reports", to: "/referral-reports", icon: FileBarChart },
    { label: "Bug Tracker", to: "/bug-tracker", icon: Bug },
  ],
  collections: [
    { label: "Collections Dashboard", to: "/dashboard/collections", icon: CreditCard },
    { label: "Bug Tracker", to: "/bug-tracker", icon: Bug },
  ],
  admin: [
    { label: "Referral Intake Dashboard", to: "/dashboard/sales", icon: TrendingUp },
    { label: "New Referral Intake", to: "/referral-intake", icon: ClipboardList },
    { label: "Manual Referral Entry", to: "/manual-referral", icon: ClipboardList },
    { label: "CRM Queue", to: "/crm-queue", icon: PhoneCall },
    { label: "SMS Needs Attention", to: "/sms-needs-attention", icon: MessageSquareWarning },
    { label: "Patient Search", to: "/patient-search", icon: Search },
    { label: "Network Scheduling", to: "/network-scheduling", icon: CalendarDays },
    { label: "Referral Reports", to: "/referral-reports", icon: FileBarChart },
    { label: "Reports", to: "/reports", icon: FileBarChart },
    { label: "User Management", to: "/admin/users", icon: Users },
    { label: "Audit Log", to: "/admin/audit", icon: ScrollText },
    { label: "Bug Tracker", to: "/bug-tracker", icon: Bug },
  ],
  location: [
    { label: "Manual Referral Entry", to: "/manual-referral", icon: ClipboardList },
    { label: "CRM Queue", to: "/crm-queue", icon: PhoneCall },
  ],
};

const ROLE_BADGE = {
  admin: "bg-purple-100 text-purple-700",
  sales: "bg-blue-100 text-blue-700",
  collections: "bg-green-100 text-green-700",
  location: "bg-amber-100 text-amber-700",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = NAV[user?.role] || [];

  // Confirmed-appointments Action Queue badge — unacknowledged network
  // confirmations. Same 30s gentle-poll cadence as the clinic sidebar
  // counts, plus an event hook so acknowledging refreshes immediately.
  const [actionQueueCount, setActionQueueCount] = useState(0);
  const hasNetworkNav = navItems.some((i) => i.to === "/network-scheduling");
  useEffect(() => {
    if (!hasNetworkNav) return undefined;
    const fetchCount = async () => {
      try {
        const res = await api.get("/scheduling/action-queue/count");
        setActionQueueCount(res.data?.count || 0);
      } catch {
        // badge is best-effort; never surface errors
      }
    };
    fetchCount();
    const handler = () => fetchCount();
    window.addEventListener("action-queue-refresh", handler);
    const t = setInterval(fetchCount, 30000);
    return () => {
      window.removeEventListener("action-queue-refresh", handler);
      clearInterval(t);
    };
  }, [hasNetworkNav]);

  // Billing module (2026-10-25): dark. The menu item appears only when the
  // server's billing gate answers 200 for this login (lib/billingAccess.js);
  // everyone else gets a 404 and sees nothing new.
  const [billingNav, setBillingNav] = useState(false);
  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setBillingNav(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  // P7 (clinic-schedule-page): dark. The menu item appears only when the server
  // says this login has a clinic on the page (CLINIC_SCHEDULE_PAGE_LOCATIONS);
  // everyone else gets a 403 and sees nothing new. Scheduling roles only.
  const [clinicScheduleNav, setClinicScheduleNav] = useState(false);
  useEffect(() => {
    let live = true;
    if (!["sales", "admin", "location"].includes(user?.role)) return undefined;
    api.get("/clinic-schedule/access")
      .then((res) => { if (live) setClinicScheduleNav((res.data?.clinics || []).length > 0); })
      .catch(() => { if (live) setClinicScheduleNav(false); });
    return () => {
      live = false;
    };
  }, [user?.id, user?.role]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-5 py-5 border-b border-slate-100">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600">
            <Shield size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              SecurePortal
            </p>
            <p className="text-xs text-slate-400 leading-tight">HIPAA Aligned</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={16} strokeWidth={1.75} />
              <span>{label}</span>
              {to === "/network-scheduling" && actionQueueCount > 0 && (
                <span
                  data-testid="network-action-queue-badge"
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold leading-none"
                >
                  {actionQueueCount}
                </span>
              )}
            </NavLink>
          ))}
          {clinicScheduleNav && (
            <NavLink
              to="/clinic-schedule"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="nav-clinic-schedule"
            >
              <CalendarDays size={16} strokeWidth={1.75} />
              <span>Clinic schedule</span>
            </NavLink>
          )}
          {billingNav && (
            <NavLink
              to="/billing"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="nav-billing"
            >
              <Receipt size={16} strokeWidth={1.75} />
              <span>Billing</span>
            </NavLink>
          )}
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-2 px-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-xs font-bold uppercase">
              {user?.username?.[0] || user?.email?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.username || user?.email}</p>
              <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium ${ROLE_BADGE[user?.role]}`}>
                {roleLabel(user?.role)}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="nav-item w-full text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={16} strokeWidth={1.75} />
            <span>Sign Out</span>
          </button>
          {/* Opt-in MFA enrollment — visible to everyone but never
              forced. Sits below Sign Out so it doesn't push other nav
              items, and keeps the security-related affordance grouped
              with the user footer. */}
          <NavLink
            to="/account/mfa-setup"
            data-testid="nav-mfa-setup"
            className={({ isActive }) =>
              `nav-item w-full text-slate-500 hover:text-slate-700 mt-1 ${isActive ? "active" : ""}`
            }
          >
            <Shield size={16} strokeWidth={1.75} />
            <span>Two-Factor Auth</span>
          </NavLink>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
